import { NavigationMenuLinks } from "@/components/NavigationMenu";
import { CustomUIMatch } from "@/hooks/useBreadcrumbs";
import { TDynamicCategory } from "@/types";

const findCategoryById = (id: string, categories?: TDynamicCategory[]) => {
  return categories?.find((c) => c.id === Number(id));
};

const findCategoryByKey = (key: string, categories?: TDynamicCategory[]) => {
  return categories?.find((c) => c.key === key);
};

// Review pages

export const publicReviewSubCategoryHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: () => NavigationMenuLinks.communicate,
  breadcrumb: ({ slug, reviewCategoryId }, categories) => {
    const category = findCategoryById(reviewCategoryId!, categories);

    return [
      {
        href: `/reviews/${slug}/public/${category?.id}`,
        label: `categories:${category?.key}`,
      },
    ];
  },
};

export const publicReviewIndexHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: () => NavigationMenuLinks.communicate,
  breadcrumb: ({ slug }) => [
    {
      href: `/reviews/${slug}/public`,
      label: "navigation-menu:pages.share",
    },
  ],
};

export const publicComparisonReviewIndexHandle: CustomUIMatch["handle"] = {
  isOverlayed: false,
  activeMenuLink: () => NavigationMenuLinks.communicate,
  breadcrumb: ({ slug }) => [
    {
      href: `/reviews/${slug}/public/comparison`,
      label: "navigation-menu:pages.comparison",
    },
  ],
};

export const reviewSubCategoryHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: () => NavigationMenuLinks.restitution,
  breadcrumb: ({ reviewId, reviewCategoryId, admin }, categories) => {
    const category = findCategoryById(reviewCategoryId!, categories);

    return [
      {
        href: `/reviews/${reviewId}/review_categories/${category?.id}${admin ? "/admin" : ""}`,
        label: `categories:${category?.key}`,
      },
    ];
  },
};

// Collect pages

export const trialHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: () => NavigationMenuLinks.collect,
  breadcrumb: () => {
    return [
      {
        href: "/trial",
        label: "categories:measure",
      },
    ];
  },
};

export const collectHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: (
    { scoreId: paramsScoreId },
    categories,
    trackingCampaignScoreId
  ) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return [
      {
        href: `/scores/${scoreId}/categories`,
        label:
          scoreId === trackingCampaignScoreId
            ? "categories:measure"
            : "pages.carbonCalculator",
      },
    ];
  },
};

export const collectGeneralInfoHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: () => {
    return [
      {
        href: "/tracking/campaigns/id/edit",
        label: "categories:generalInfo",
      },
    ];
  },
};

export const collectCategoryHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId, categoryId }, categories) => {
    const category = findCategoryById(categoryId!, categories);

    return [
      {
        href: `/scores/${scoreId}/categories/${category?.id}`,
        label: `categories:${category?.key}`,
      },
    ];
  },
};

export const collectSurveyHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId, subCategory }, categories) => {
    const category = findCategoryByKey(subCategory!, categories);

    return [
      {
        href: `/scores/${scoreId}/categories/${category?.root_category?.id}`,
        label: `categories:${category?.root_category?.key}`,
      },
    ];
  },
};

export const collectSurveyCreateHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ subCategory }, categories) => {
    const scoreId = localStorage.getItem("currentScoreId");
    const category = findCategoryByKey(subCategory!, categories);

    return [
      {
        href: `/scores/${scoreId}/categories`,
        label: "categories:measure",
      },
      {
        href: `/scores/${scoreId}/categories/${category?.root_category?.id}`,
        label: `categories:${category?.root_category?.key}`,
      },
      {
        href: `/scores/${scoreId}/survey/${subCategory}`,
        label: `categories:${subCategory}`,
      },
      {
        href: `/scores/${scoreId}/survey/${subCategory}/new`,
        label: "categories:new",
      },
    ];
  },
};

export const collectSubCategoryHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId, subCategory }) => {
    return [
      {
        href: `/scores/${scoreId}/survey/${subCategory}`,
        label: `categories:${subCategory}`,
      },
    ];
  },
};

export const collectEmployeeSurveyActivationHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId }) => {
    return [
      {
        href: `/scores/${scoreId}/team_mode`,
        label: "categories:team",
      },
    ];
  },
};

export const collectEmployeeSurveyHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId }) => {
    return [
      {
        href: `/scores/${scoreId}/survey/team/report`,
        label: "categories:team",
      },
    ];
  },
};

export const collectAnalysisImportPreviewHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId, analysisId }) => {
    return [
      {
        href: `/scores/${scoreId}/import_deposits`,
        label: "categories:importDeposits.files",
      },
      {
        href: `/scores/${scoreId}/analysis_imports/${analysisId}/preview`,
        label: "categories:importDeposits.summary",
      },
    ];
  },
};

export const collectImportedDepositsHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId }) => {
    return [
      {
        href: `/scores/${scoreId}/import_deposits`,
        label: "categories:importDeposits.files",
      },
    ];
  },
};

export const collectImportedNewHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId }) => {
    return [
      {
        href: `/scores/${scoreId}/import_deposits`,
        label: "categories:importDeposits.files",
      },
      {
        href: `/scores/${scoreId}/import_deposits/new`,
        label: "categories:importDeposits.new",
      },
    ];
  },
};
export const collectImportSummaryHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId, importId }) => {
    return [
      {
        href: `/scores/${scoreId}/import_deposits`,
        label: "categories:importDeposits.files",
      },
      {
        href: `/scores/${scoreId}/import_deposits/${importId}`,
        label: "categories:importDeposits.summary",
      },
    ];
  },
};

export const collectImportMatchingHandle: CustomUIMatch["handle"] = {
  isOverlayed: true,
  activeMenuLink: ({ scoreId: paramsScoreId }, trackingCampaignScoreId) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return scoreId === trackingCampaignScoreId
      ? NavigationMenuLinks.collect
      : NavigationMenuLinks.simulate;
  },
  breadcrumb: ({ scoreId, importId }) => {
    return [
      {
        href: `/scores/${scoreId}/import_deposits`,
        label: "categories:importDeposits.files",
      },
      {
        href: `/scores/${scoreId}/import_deposits/${importId}`,
        label: "categories:importDeposits.summary",
      },
      {
        href: `/scores/${scoreId}/import_deposits/${importId}/matching`,
        label: "categories:importDeposits.matching",
      },
    ];
  },
};
